// The start / stop of one Case or Action inside a Block's detail (several can
// be worked on at once): whether it is being worked on now, and how long it took. Pressing is optional (the
// review stands without it), so it stays small and shows on hover.
import type {
  TimeBlock,
  TimeBlockLink,
} from "@trameria/core/timeblocker-shared";
import { m } from "@trameria/i18n";
import { durationText } from "../agent-chat/names";
import { IconButton } from "../ui/button";
import { Icon } from "../ui/icon";
import { useElapsedMs, useMinuteClock } from "./clock";
import { useBlockWork } from "./queries";
import { useTimeBlockerScreen } from "./screen-state";
import { openWorks, sameTarget, workedMs } from "./works";

export function WorkControl({
  block,
  link,
  onError,
}: {
  block: TimeBlock;
  link: TimeBlockLink;
  onError(e: Error): void;
}) {
  const screen = useTimeBlockerScreen();
  const work = useBlockWork();
  const minute = useMinuteClock().getTime();
  const open = openWorks(block, minute).find((w) => sameTarget(w, link));
  const active = !!open;
  // The seconds turn over with the Block's own (as the header and the timeline do).
  const running = useElapsedMs(active && open ? open.startedAt : null);
  const over = !!block.endedAt || Date.parse(block.endAt) <= minute;
  const total =
    active && open
      ? workedMs(block, link, Date.parse(open.startedAt)) + running
      : workedMs(block, link, minute);
  const title = link.stepTitle ?? link.caseTitle;
  const target = { caseId: link.caseId, stepId: link.stepId };
  return (
    <span className="flex flex-none items-center gap-1">
      {active ? (
        <>
          <span className="inline-flex items-center gap-1 text-[11px] tabular-nums text-accent-deep">
            <span
              aria-hidden="true"
              className="h-[6px] w-[6px] animate-pulse rounded-full bg-accent"
            />
            {m.timeblocker_item_working()} {durationText(total)}
          </span>
          <IconButton
            label={m.timeblocker_item_stop({ title })}
            onClick={() =>
              work.stop({ ...screen.place, block, target }, { onError })
            }
          >
            <Icon kind="stop" size={10} />
          </IconButton>
        </>
      ) : (
        <>
          {total > 0 && (
            <span className="text-[11px] tabular-nums text-faint">
              {durationText(total)}
            </span>
          )}
          {!over && (
            <span className="opacity-0 focus-within:opacity-100 group-hover/row:opacity-100 pointer-coarse:opacity-100">
              <IconButton
                label={m.timeblocker_item_start({ title })}
                onClick={() =>
                  work.start({ ...screen.place, block, target }, { onError })
                }
              >
                <Icon kind="play" size={10} />
              </IconButton>
            </span>
          )}
        </>
      )}
    </span>
  );
}
